import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { isApiError } from '@/utils/ApiError';

/**
 * TanStack Query owns all server state. There is no Redux store: the only
 * genuinely client-side state in this application is the authenticated user,
 * which lives in a small context.
 */
export function QueryProvider({ children }: { children: ReactNode }) {
  // Created in state so a re-render never replaces the client and discards the cache.
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            gcTime: 5 * 60_000,
            refetchOnWindowFocus: false,
            retry: (failureCount, error) => {
              /*
               * Retrying a 4xx is pointless — the request will be rejected the
               * same way every time — and actively harmful for 401, where each
               * attempt triggers another token refresh. Only genuine transport
               * and server failures are worth a second try.
               */
              if (isApiError(error) && error.status >= 400 && error.status < 500) {
                return false;
              }
              return failureCount < 2;
            },
          },
          mutations: {
            // A mutation is a user-initiated action; silently repeating it could
            // create a duplicate record.
            retry: false,
          },
        },
      }),
  );

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}
